/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useContext, useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { editTableCol, doHideEditbar } from '../../../builder/reducers/fieldSettings';
import { doAddTableCol, doDeleteTableCol } from '../../../builder/reducers/builder';
import { addTableRow, deleteTableRow } from '../../../form/reducer/form';
import { doResetControl } from '../../../builder/slice-actions/editbar';
import { FormContext } from '../../../shared/contexts/form-context';
import { selectCtrlIdLabelMap } from '../../selectors';
import { ControlPropType } from '../../ctrl_proptype';
import { getNumbers } from '../../../../shared/utils';
import { getSumOrAverage } from '../../../form/utils';
import { toHex } from '../../../../shared/form-lib';
import {
  Table,
  TableHeader,
  TableBody,
  TableHeaderSpan,
  FieldRenderer,
  TableSubWrapper,
  AddColBtn,
  ColBtn,
  AddRowButton,
  DeleteRowButton,
  TableFoot,
} from './table_field.styled';
import TableAllowedFields from './allowed_fields';
import ColumnHead from './column_head';

export default function TableFieldSubComponent(props) {
  const { ctrl, formDesign } = props;
  const userCustomTexts = useSelector((state) => state.form.userCustomTexts);
  const { activePage, inPreviewPage: previewMode, previewViewPort } = useSelector((state) => state.builder.present);
  const { control, showEditbar } = useSelector((state) => state.fieldEdit);
  const ctrlIdLabelMap = useSelector(selectCtrlIdLabelMap);
  const dispatch = useDispatch();
  const tableId = ctrl.internal_id;
  const { inForm, inBuilder, currentPageNo } = useContext(FormContext);
  const [displayedRow, setDisplayedRow] = useState(
    !ctrl.value ? getNumbers(ctrl.props.min_rows) : getNumbers(ctrl.value.length)
  );
  
  const isPreviewMobile = previewMode && previewViewPort === 'mobile';
  const columnHasCalculation = () => ctrl.table_props.find((ct) => ct.column_calculation > 0);
  const hasRowTitles = ctrl.props.show_row_headers;
  const disableAddRow = ctrl.props?.disable_add_row;

  const customButtonStyles = !inBuilder
    ? {
        '--btn-color': toHex(formDesign.design.secondary_button_color),
        '--btn-bg': toHex(formDesign.design.secondary_button_background_color),
        '--btn-font': formDesign.old.form_font,
      }
    : undefined;

  useEffect(() => {
    setDisplayedRow(!ctrl.value ? getNumbers(ctrl.props.min_rows) : getNumbers(ctrl.value.length));
  }, [ctrl.props.min_rows]);

  const handleAddNewRow = () => {
    setDisplayedRow([...displayedRow, displayedRow[displayedRow.length - 1] + 1]);
    dispatch(
      addTableRow({
        page: currentPageNo - 1,
        tableId,
      })
    );
  };

  const handleDeleteRow = (i) => {
    if (inForm) {
      const filteredList = displayedRow.filter((item) => item !== displayedRow[i]);
      setDisplayedRow(getNumbers(filteredList.length));
      dispatch(
        deleteTableRow({
          i,
          page: currentPageNo - 1,
          tableId,
        })
      );
    }
  };

  const doEditCol = (colIndex) => {
    dispatch(editTableCol({ editedId: tableId, control: ctrl, colIndex }));
  };

  const tableColumnRemove = (colIndex) => {
    batch(() => {
      if (showEditbar) {
        dispatch(doResetControl(control));
        dispatch(doHideEditbar());
      }

      dispatch(doDeleteTableCol({
        pgIndex: activePage,
        colIndex,
        tableId
      }));
    });
  };

  const addTableColumn = (e) => {
    e.stopPropagation();
    dispatch(doAddTableCol({ pgIndex: activePage, tableId }));
  };

  const renderField = (tableControl, tr, col) => {
    const isStacked = ctrl.props.stacked && !ctrl.props.unstackable;
    const showLabel = isStacked || (inBuilder && isPreviewMobile);
    const fieldType = tableControl.props.column_types[col - 1];
    const Field = TableAllowedFields[fieldType];
    const className = [
      !inBuilder ? 'not-in-builder' : '',
      showLabel ? 'show' : '',
      'table-ctrl-label'
    ].join(' ');

    if (!Field) {
      throw new Error(`${fieldType} isn't allowed in table`);
    }

    return (
      <>
        <label className={className}>{ctrl.props.column_headers[col - 1]}</label>
        <Field ctrl={tableControl} inTable col={col} tr={tr} formDesign={formDesign} />
      </>
    );
  };

  const handleEditColumn = (e, th) => {
    e.stopPropagation();
    doEditCol(th - 1);
  };

  const handleDeleteColumn = (e, th) => {
    e.stopPropagation();
    tableColumnRemove(th - 1);
  };

  const renderColumnHeader = (headerValue, th) => {
    const columnHeader = ctrlIdLabelMap?.[ctrl.internal_id]?.[th - 1] || headerValue;
    return (
      <>
        {!inBuilder && (
          <TableHeaderSpan
            dangerouslySetInnerHTML={{ __html: columnHeader }}
            requiredStar={ctrl.table_props[th - 1]?.required}
            notInBuilder={!inBuilder}
          />
        )}

        {previewMode && (
          <div>
            <ColumnHead th={th} ctrl={ctrl} previewMode={previewMode} pgIndex={activePage} headerValue={headerValue} />
          </div>
        )}

        {inBuilder && !previewMode && (
          <div>
            <ColumnHead th={th} ctrl={ctrl} previewMode={previewMode} pgIndex={activePage} headerValue={headerValue} />
            {th && (
              <div>
                <ColBtn onClick={(e) => handleEditColumn(e, th)} title="Edit Column">
                  <img src="https://storage.googleapis.com/fplsblog/edit_icon.svg" alt="" />
                </ColBtn>
                <ColBtn onClick={(e) => handleDeleteColumn(e, th)} title="Remove Column">
                  <img src="https://storage.googleapis.com/fplsblog/delete_icon.svg" alt="" />
                </ColBtn>
              </div>
            )}
          </div>
        )}
      </>
    );
  };
  
  return (
    <>
      <TableSubWrapper id={`fpl-${tableId}`}>
        <Table
          customTableWidth={ctrl.props.table_width}
          responsiveTable={!ctrl.props.unstackable}
          noBorder={ctrl.props.no_table_border}
          isPreviewMobile={isPreviewMobile}
          unstacked={!ctrl.props.stacked}
          unstackable={ctrl.props.unstackable}
          columnCount={ctrl.props.columns}
          displayedRow={displayedRow.length > 1}
          hasRowTitles={hasRowTitles}
          disableAddRow={disableAddRow}>
          <TableHeader
            stacked={ctrl.props.stacked}
            unstackable={!ctrl.props.unstackable}
            noBorder={ctrl.props.no_table_border}
            isPreviewMobile={isPreviewMobile}>
            <tr>
              {hasRowTitles && <th>{renderColumnHeader(ctrl.props.row_headers_column_header, null)}</th>}

              {getNumbers(ctrl.props.columns).map((th) => (
                <th
                  key={th}
                  style={{
                    width: !ctrl.props.stacked &&
                    ctrl.props.column_widths[th - 1] > 0 &&
                    `${ctrl.props.column_widths[th - 1]}%`
                  }}
                >
                  {renderColumnHeader(ctrl.props.column_headers[th - 1], th)}
                </th>
              ))}

              {inBuilder && ctrl.props.columns < 15 && !previewMode && (
                <th style={{ width: '63px'}}>
                  <AddColBtn type="button" onClick={addTableColumn}>
                    <img src="https://storage.googleapis.com/fplsblog/plus_icon.svg" alt="" />
                  </AddColBtn>
                </th>
              )}

              {!inBuilder && displayedRow.length > 1 && !hasRowTitles && !ctrl.props?.disable_add_row && (
                <th style={{ visibility: 'hidden' }}> dummy </th>
              )}
            </tr>
          </TableHeader>
          <TableBody
            stacked={ctrl.props.stacked && !ctrl.props.unstackable}
            noBorder={ctrl.props.no_table_border}
            unstackable={ctrl.props.unstackable}
            isPreviewMobile={isPreviewMobile}
            columnCount={ctrl.props.columns}
            inForm={inForm}>
            {displayedRow.map((tr, i) => (
              <tr key={tr}>
                {hasRowTitles && <td className="title">{ctrl.props.row_headers[i]}</td>}

                {getNumbers(ctrl.props.columns).map((col) => (
                  <td key={col}>
                    <FieldRenderer requiredStar={ctrl.table_props[col - 1].required}>
                      {renderField(ctrl, tr, col)}
                    </FieldRenderer>
                  </td>
                ))}
                {displayedRow.length > 1 && !hasRowTitles && !ctrl.props?.disable_add_row && (
                  <td className="remove-icon">
                    <DeleteRowButton
                      type="button"
                      style={customButtonStyles}
                      className={`${!inForm && 'no-cursor'}`}
                      inForm={inForm}
                      tabIndex={0}
                      onClick={() => handleDeleteRow(i)}>
                      <i className="remove icon" />
                    </DeleteRowButton>
                  </td>
                )}
              </tr>
            ))}
          </TableBody>

          {columnHasCalculation() && (
            <TableFoot stacked={ctrl.props.stacked && !ctrl.props.unstackable} isPreviewMobile={isPreviewMobile}>
              <tr>
                {hasRowTitles && <td> </td>}

                {getNumbers(ctrl.props.columns).map((col, index) => (
                  <td key={col}>
                    {ctrl.table_props[index].column_calculation === 1 && (
                      <>
                        <span>SUM:</span>
                        <span> {getSumOrAverage(ctrl, index, 'sum')}</span>
                      </>
                    )}
                    {ctrl.table_props[index].column_calculation === 2 && (
                      <>
                        <span>AVERAGE:</span>
                        <span> {getSumOrAverage(ctrl, index, 'average')}</span>
                      </>
                    )}
                  </td>
                ))}
              </tr>
            </TableFoot>
          )}
        </Table>
      </TableSubWrapper>

      {!inBuilder && !ctrl.props?.disable_add_row && ctrl.props.max_rows > displayedRow.length && !hasRowTitles && (
        <AddRowButton
          onClick={handleAddNewRow}
          disabled={!inForm}
          hasCustomBackground={customButtonStyles['--btn-bg']}
          style={customButtonStyles}>
          <i className="ui add icon" />
          {userCustomTexts.add_new_row || 'Add New Row'}
        </AddRowButton>
      )}
    </>
  );
}

TableFieldSubComponent.defaultProps = {
  formDesign: null,
};

TableFieldSubComponent.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};
